// Reine Format-/Label-Helfer fuer das neue Dashboard-Serien-Modul (Task 13/14 Backend,
// api/dashboard.ts DASH_*-Exporte). Bewusst NICHT in src/lib/timeseries-format.ts
// erweitert — das ist die Formatierung des AELTEREN /system/timeseries/-Endpoints
// (andere Metriken: orders/revenue/returns/units, andere Ranges 1M..36M) und bleibt
// unangetastet. formatBucketLabel + UNIT_LABELS von dort werden weiterverwendet (siehe
// Overview.tsx), weil das Bucket-Label-Format (YYYY-MM-DD, day/week/month) identisch ist.
//
// Verschoben von pages/overview/ nach lib/ (Task 16): Overview UND die neue Artikel-
// Analyse-Seite (pages/analytics/ArticleAnalysis.tsx) brauchen beide DASH_RANGE_LABELS +
// formatDashValue/formatDashPercent — das ist ein seitenuebergreifender Formatierungs-
// Baustein, kein Overview-internes Detail mehr.
import type { DashRange, DashSeriesMetric, DashSeriesBucket } from "@/api/dashboard";

// Range-Labels — Spiegel der 8 Presets aus DASH_RANGES (api/dashboard.ts, Quelle
// src/modules/dashboard/logic.ts RANGE_MAP).
export const DASH_RANGE_LABELS: Record<DashRange, string> = {
  "1T": "1 Tag",
  "7T": "7 Tage",
  "30T": "30 Tage",
  "90T": "90 Tage",
  "6M": "6 Monate",
  "12M": "12 Monate",
  "24M": "24 Monate",
  "36M": "36 Monate",
};

// Metrik-Labels/-Untertitel — Spiegel von DASH_SERIES_METRICS (api/dashboard.ts, Quelle
// src/modules/dashboard/service.ts: buildOrderSeriesSql fuer revenue/credits,
// buildLineSeriesSql fuer units/abgang_ek).
export const DASH_METRIC_LABELS: Record<DashSeriesMetric, string> = {
  revenue: "Umsatz",
  credits: "Gutschriften",
  units: "Verkaufte Stück",
  abgang_ek: "Lagerabgang (EK)",
};

export const DASH_METRIC_SUBTITLES: Record<DashSeriesMetric, string> = {
  revenue: "Bruttowarenwert je Verkaufskanal",
  credits: "Gutschriften je Verkaufskanal — unterhalb der Nulllinie dargestellt",
  units: "Verkaufte Stückzahl je Verkaufskanal (Grundartikel-umgeschlüsselt)",
  abgang_ek: "Lagerabgang zu Einkaufspreisen je Verkaufskanal",
};

// Welle 1, #7-Vorgriff (Befund B1 der Spec): Auftragsdaten liegen in der Plattform-DB
// erst ab September 2025 vollstaendig vor (davor nur Streu-Einzelfaelle). Bis der
// DR-Backfill (Welle 3, gated) gelaufen ist, muss jede Langzeit-Ansicht das ehrlich
// ausweisen, statt eine leere Achse als "kein Umsatz" erscheinen zu lassen.
export const HISTORY_HINT_TEXT =
  "Auftragsdaten liegen erst ab September 2025 vor — längere Zeiträume zeigen davor keine Werte.";

export function needsHistoryHint(range: DashRange): boolean {
  return range === "24M" || range === "36M";
}

const CURRENCY_METRICS = new Set<DashSeriesMetric>(["revenue", "credits", "abgang_ek"]);

// Ganze Euro, de-DE — Konvention der bestehenden formatMetricValue (lib/timeseries-format.ts),
// hier als eigenstaendiger Baustein uebernommen, damit KPI-Karten (Lagerwert EK,
// Gutschrift-Betraege) und Chart-Werte dieselbe Darstellung nutzen.
export function formatEuro(value: number): string {
  return value.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

export function formatDashValue(metric: DashSeriesMetric, value: number): string {
  if (CURRENCY_METRICS.has(metric)) return formatEuro(value);
  return value.toLocaleString("de-DE");
}

export function formatDashPercent(fraction: number): string {
  return `${(fraction * 100).toFixed(1).replace(".", ",")} %`;
}

// Summiert alle Kanalwerte eines Buckets ueber die angegebene Kanal-Liste (nur diese —
// Kanaele ausserhalb der Liste werden ignoriert, analog zum Vertrag von
// DashSeriesPayload.channels: die Liste ist die vollstaendige, gueltige Menge). Bei
// metric='credits' liefert das bewusst eine negative Summe (Gutschriften mindern die
// Bilanz), bei den anderen drei Metriken eine positive.
export function sumDashBuckets(buckets: DashSeriesBucket[], channels: string[]): number {
  let sum = 0;
  for (const b of buckets) {
    for (const ch of channels) sum += b.values[ch] ?? 0;
  }
  return sum;
}
